/**
 * Staff — Meeting Rooms / Salles management (iter-52 · Vague 3).
 *
 * Admin/manager-only CRUD for the meeting rooms catalog. Edits propagate
 * immediately to /univers/salles (public booking page).
 */
import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Save, X, DoorOpen, Users } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const fmtXof = (n) => new Intl.NumberFormat("fr-FR").format(Number(n || 0)) + " FCFA";

const EMPTY_ROOM = {
  name: "",
  capacity_max: 50,
  price_half_day_xof: 200000,
  price_full_day_xof: 350000,
  description: "",
  image_url: "",
  active: true,
  layouts: [],
  equipment: [],
};

export default function StaffMeetingRooms() {
  const [rooms, setRooms] = useState([]);
  const [layoutsCatalog, setLayoutsCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null); // "new" or room.id
  const [form, setForm] = useState(EMPTY_ROOM);
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [list, cat] = await Promise.all([
        api.get("/staff/meeting-rooms"),
        api.get("/experiences/meeting-rooms/catalog"),
      ]);
      setRooms(list.data || []);
      setLayoutsCatalog(cat.data?.layouts || []);
    } catch {
      toast.error("Impossible de charger les salles");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditingId("new"); setForm(EMPTY_ROOM); };
  const startEdit = (r) => {
    setEditingId(r.id);
    setForm({
      name: r.name || "",
      capacity_max: r.capacity_max || 50,
      price_half_day_xof: r.price_half_day_xof || 0,
      price_full_day_xof: r.price_full_day_xof || 0,
      description: r.description || "",
      image_url: r.image_url || "",
      active: r.active !== false,
      layouts: r.layouts || [],
      equipment: r.equipment || [],
    });
  };

  const cancel = () => { setEditingId(null); setForm(EMPTY_ROOM); };

  const toggleLayout = (layoutId, capacity = null) => {
    const exists = form.layouts.find((l) => l.layout_id === layoutId);
    if (exists) {
      setForm({ ...form, layouts: form.layouts.filter((l) => l.layout_id !== layoutId) });
    } else {
      setForm({ ...form, layouts: [...form.layouts, { layout_id: layoutId, capacity: capacity || form.capacity_max }] });
    }
  };
  const setLayoutCapacity = (layoutId, capacity) => {
    setForm({ ...form, layouts: form.layouts.map((l) => l.layout_id === layoutId ? { ...l, capacity } : l) });
  };
  const setEquipmentText = (txt) => {
    setForm({ ...form, equipment: txt.split("\n").map((s) => s.trim()).filter(Boolean) });
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nom requis"); return; }
    setSaving(true);
    try {
      if (editingId === "new") {
        await api.post("/staff/meeting-rooms", form);
        toast.success("Salle créée");
      } else {
        await api.patch(`/staff/meeting-rooms/${editingId}`, form);
        toast.success("Salle mise à jour");
      }
      cancel();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Supprimer la salle « ${r.name} » ? Cette action est irréversible.`)) return;
    try {
      await api.delete(`/staff/meeting-rooms/${r.id}`);
      toast.success("Salle supprimée");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Suppression impossible");
    }
  };

  return (
    <div className="p-4 md:p-8 lg:p-10 max-w-7xl mx-auto" data-testid="staff-meeting-rooms">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-serif text-3xl flex items-center gap-3"><DoorOpen className="w-7 h-7" />Salles de séminaire</h1>
          <p className="text-sm text-neutral-500 mt-1">Gérez le catalogue des salles publiées sur <code>/univers/salles</code>.</p>
        </div>
        <button
          data-testid="add-room-btn"
          onClick={startCreate}
          className="bg-[#0A0A0A] text-white px-4 py-2 rounded-full text-sm flex items-center gap-2 hover:bg-[#1A1A1A]"
        >
          <Plus className="w-4 h-4" /> Nouvelle salle
        </button>
      </div>

      {loading ? (
        <div className="text-neutral-500">Chargement…</div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="rooms-grid">
          {rooms.map((r) => (
            <div key={r.id} data-testid={`room-card-${r.id}`} className={`border rounded-2xl overflow-hidden ${r.active ? "border-neutral-200" : "border-neutral-200 bg-neutral-50 opacity-70"}`}>
              <div className="aspect-[4/3] bg-cover bg-center bg-neutral-100" style={{ backgroundImage: `url(${r.image_url || ""})` }} />
              <div className="p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-serif text-xl">{r.name}</h3>
                    <p className="text-xs text-neutral-500 flex items-center gap-1 mt-0.5"><Users className="w-3 h-3" />{r.capacity_max} pax max · {r.layouts?.length || 0} dispos</p>
                  </div>
                  {!r.active && <span className="text-xs text-neutral-500 bg-neutral-200 px-2 py-0.5 rounded">Inactif</span>}
                </div>
                <div className="text-sm mt-3 space-y-0.5">
                  <div className="flex justify-between"><span>½ journée</span><span className="tabular-nums">{fmtXof(r.price_half_day_xof)}</span></div>
                  <div className="flex justify-between"><span>Journée</span><span className="tabular-nums">{fmtXof(r.price_full_day_xof)}</span></div>
                </div>
                <div className="flex gap-2 mt-4">
                  <button data-testid={`edit-room-${r.id}`} onClick={() => startEdit(r)} className="flex-1 text-sm bg-[#FAF6E8] border border-[#B8922A]/30 px-3 py-2 rounded-lg flex items-center justify-center gap-1 hover:bg-[#F4ECD3]"><Pencil className="w-3 h-3" />Modifier</button>
                  <button data-testid={`delete-room-${r.id}`} onClick={() => remove(r)} className="text-sm bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg flex items-center gap-1 hover:bg-red-100"><Trash2 className="w-3 h-3" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit modal */}
      {editingId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={cancel}>
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="room-edit-modal">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-serif text-2xl">{editingId === "new" ? "Nouvelle salle" : "Modifier la salle"}</h2>
              <button onClick={cancel}><X className="w-5 h-5" /></button>
            </div>

            <div className="space-y-4">
              <label className="block">
                <span className="text-sm font-medium">Nom de la salle</span>
                <input data-testid="room-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border border-neutral-300 rounded-md" />
              </label>

              <label className="block">
                <span className="text-sm font-medium">Description</span>
                <textarea data-testid="room-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={2} className="w-full mt-1 px-3 py-2 border border-neutral-300 rounded-md text-sm" />
              </label>

              <div className="grid grid-cols-3 gap-3">
                <label className="block">
                  <span className="text-sm font-medium">Capacité max</span>
                  <input type="number" data-testid="room-capacity" value={form.capacity_max} onChange={(e) => setForm({ ...form, capacity_max: parseInt(e.target.value) || 0 })}
                    className="w-full mt-1 px-3 py-2 border border-neutral-300 rounded-md" />
                </label>
                <label className="block">
                  <span className="text-sm font-medium">Prix ½ journée (FCFA)</span>
                  <input type="number" data-testid="room-price-half" value={form.price_half_day_xof} onChange={(e) => setForm({ ...form, price_half_day_xof: parseInt(e.target.value) || 0 })}
                    className="w-full mt-1 px-3 py-2 border border-neutral-300 rounded-md" />
                </label>
                <label className="block">
                  <span className="text-sm font-medium">Prix journée (FCFA)</span>
                  <input type="number" data-testid="room-price-full" value={form.price_full_day_xof} onChange={(e) => setForm({ ...form, price_full_day_xof: parseInt(e.target.value) || 0 })}
                    className="w-full mt-1 px-3 py-2 border border-neutral-300 rounded-md" />
                </label>
              </div>

              <label className="block">
                <span className="text-sm font-medium">URL image de présentation</span>
                <input data-testid="room-image" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                  placeholder="https://..."
                  className="w-full mt-1 px-3 py-2 border border-neutral-300 rounded-md text-sm" />
              </label>

              <div>
                <p className="text-sm font-medium mb-2">Dispositions disponibles</p>
                <div className="grid grid-cols-2 gap-2" data-testid="room-layouts">
                  {layoutsCatalog.map((lay) => {
                    const sel = form.layouts.find((l) => l.layout_id === lay.id);
                    return (
                      <div key={lay.id} className={`p-3 rounded-lg border ${sel ? "border-[#B8922A] bg-[#FAF6E8]" : "border-neutral-200"}`}>
                        <label className="flex items-center gap-2 text-sm font-medium">
                          <input type="checkbox" data-testid={`layout-toggle-${lay.id}`} checked={!!sel} onChange={() => toggleLayout(lay.id)} />
                          {lay.name_fr}
                        </label>
                        {sel && (
                          <div className="mt-2 flex items-center gap-2 text-xs">
                            <span>Capacité :</span>
                            <input type="number" data-testid={`layout-capacity-${lay.id}`} value={sel.capacity} onChange={(e) => setLayoutCapacity(lay.id, parseInt(e.target.value) || 0)}
                              className="w-20 px-2 py-1 border rounded" />
                            <span>pax</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <label className="block">
                <span className="text-sm font-medium">Équipement (un par ligne)</span>
                <textarea data-testid="room-equipment" value={(form.equipment || []).join("\n")} onChange={(e) => setEquipmentText(e.target.value)}
                  rows={4} className="w-full mt-1 px-3 py-2 border border-neutral-300 rounded-md text-sm font-mono" />
              </label>

              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" data-testid="room-active" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                Salle publiée (visible sur le site public)
              </label>
            </div>

            <div className="flex gap-3 mt-6 pt-6 border-t">
              <button data-testid="save-room" disabled={saving} onClick={save}
                className="flex-1 bg-[#0A0A0A] text-white py-3 rounded-full flex items-center justify-center gap-2 hover:bg-[#1A1A1A] disabled:opacity-50">
                <Save className="w-4 h-4" />{saving ? "Enregistrement…" : "Enregistrer"}
              </button>
              <button onClick={cancel} className="px-6 py-3 border rounded-full">Annuler</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
